/**
 * ScoutingReportsListView - Displays list of scouting reports
 *
 * Renders a card containing a list of scouting reports with details
 * including date, opponent, report type, and author. Clicking a report
 * navigates to the full reports page. "View all" opens a FullScreenOverlay
 * modal displaying all reports. Add Report navigates to the existing
 * matches page for report creation. The report count badge uses the same
 * rounded-full grey-background pill style as the Notes section for visual
 * consistency (SE-3883).
 *
 * The list body is drawn from a single read state (SE-7872): a failed read now
 * renders a retryable error instead of "No scouting reports yet". The empty
 * copy is unchanged — a player with no reports is a real answer and stays
 * legible; what changed is that a failure no longer borrows it.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { FileText, Plus } from "lucide-react-native";
import React, { memo } from "react";

import type {
  ScoutingReport,
  ScoutReportsReadState,
} from "@/features/player-detail/types";
import ScoutingReportRow from "@/features/player-detail/components/molecules/ScoutingReportRow";
import PlayerDetailErrorState from "@/features/player-detail/components/molecules/PlayerDetailErrorState";

import FullScreenOverlay from "@/components/organisms/shared/FullScreenOverlay";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Props for ScoutingReportsListView component
 */
interface ScoutingReportsListViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Array of visible scouting reports (limited) */
  readonly reports: readonly ScoutingReport[];
  /** Array of all scouting reports */
  readonly allReports: readonly ScoutingReport[];
  /** Total count of reports */
  readonly reportCount: number;
  /** Whether there are more reports than displayed */
  readonly hasMoreReports: boolean;
  /** Whether to show the View All modal */
  readonly showViewAllModal: boolean;
  /** Callback when Add Report button is pressed - navigates to matches page */
  readonly onAddReport: () => void;
  /** Callback when a report is pressed - navigates to full reports page */
  readonly onReportPress: (reportId: string) => void;
  /** Callback when View all is pressed - opens modal */
  readonly onViewAll: () => void;
  /** Callback when View All modal is closed */
  readonly onCloseViewAll: () => void;
  /**
   * Which of the read's outcomes the list body should draw (SE-7872). The
   * count badge is suppressed for the two unsettled states — "0" next to a
   * failed read is the same false claim in miniature.
   */
  readonly readState: ScoutReportsReadState;
  /** Retries the read behind this card, from the error body */
  readonly onRetry: () => void;
  /** Whether a retry is in flight — drives the error CTA busy state */
  readonly isRetrying: boolean;
  /**
   * Whether the card is one of two side-by-side columns rather than a block in
   * a stack. Only then may it take a zero flex basis — see
   * {@link getContainerClass}.
   */
  readonly isSideBySide?: boolean;
}

/**
 * Get container background class.
 *
 * SE-6910: `flex-1` is applied only in the side-by-side layout. It is
 * `flex: 1 1 0%` — a zero basis — so the card's box is sized by flex
 * distribution rather than by its content. In the horizontal layout that is
 * what makes the two columns share the row's width; in the STACKED layout the
 * main axis is vertical, so it makes the card report a height its own content
 * does not fit inside, and the overflow paints below the box without
 * lengthening the scroll extent. See the fuller note on the sibling notes card,
 * which is where that was measured.
 * @param isSideBySide - Whether the card is one of two side-by-side columns
 */
const getContainerClass = (isSideBySide: boolean): string =>
  isSideBySide
    ? "flex-1 rounded-lg border border-outline-card bg-surface-card p-4"
    : "rounded-lg border border-outline-card bg-surface-card p-4";

/**
 * Get title text class
 *
 * Size and weight come from the `title-sm` text-style variant on the consuming
 * `Text` atom — this returns the theme color only.
 */
const getTitleClass = (): string => "text-content-primary";

/**
 * Get count badge container class matching Notes section badge style
 */
const getCountBadgeClass = (): string =>
  "rounded-full bg-surface-muted px-2 py-micro";

/**
 * Get count badge text class matching Notes section badge style
 */
const getCountTextClass = (): string =>
  "text-caption font-medium text-content-secondary";

/**
 * Get add button class (pill-shaped, below header)
 * Uses gluestack Button with outline variant
 */
const getAddButtonClass = (): string =>
  "self-start rounded-full border-surface-muted bg-surface-raised data-[hover=true]:bg-surface-muted";

/**
 * Get view all link class
 */
const getViewAllClass = (): string => "text-accent-primary";

/**
 * Get icon color based on theme
 * @param isDark - Whether dark mode is active
 */
const getIconColor = (isDark: boolean): string =>
  isDark ? "#9CA3AF" : "#6B7280";

/**
 * Get empty state text class
 */
const getEmptyStateClass = (): string => "py-8 text-center text-content-muted";

/** Copy for the `failed` body (SE-7872). */
const REPORTS_ERROR_TITLE = "Couldn't load scouting reports";
/**
 * Subtitle for the `failed` body.
 *
 * "That is not the same as there being none" is load-bearing, and is the same
 * sentence the Similar Players card uses (SE-7580): it is the whole reason
 * `failed` and `empty` are separate bodies rather than one "nothing here"
 * message.
 */
const REPORTS_ERROR_SUBTITLE =
  "We couldn't load this player's scouting reports. That is not the same as there being none — try again.";
/** Copy shown while the read is outstanding. */
const REPORTS_LOADING_MESSAGE = "Loading scouting reports…";

/** What {@link renderReportsBody} needs to draw any one of the four bodies. */
interface ReportsBodyProps {
  /** The read's state, which decides which body renders. */
  readonly readState: ScoutReportsReadState;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Reports to draw, for the one state that has any. */
  readonly reports: readonly ScoutingReport[];
  /** Whether a retry is in flight */
  readonly isRetrying: boolean;
  /** Retries the read */
  readonly onRetry: () => void;
  /** Callback when a report is pressed */
  readonly onReportPress: (reportId: string) => void;
}

/**
 * Renders the list body for the given read state (SE-7872).
 *
 * Block-bodied on purpose — it is a four-way switch with an exhaustiveness
 * check, matching the shape the Similar Players list settled on. `empty` keeps
 * the exact copy it always had: a player with no reports yet is a real and
 * common answer, and this change is about stopping a failed read borrowing
 * that answer, not about restyling it.
 * @param props - Body props.
 * @returns The list body.
 */
const renderReportsBody = (props: ReportsBodyProps): React.ReactElement => {
  switch (props.readState) {
    case "loading":
      return (
        <Stack className="items-center justify-center gap-2 py-8">
          <Spinner size="small" />
          <Text variant="caption" className="text-content-muted">
            {REPORTS_LOADING_MESSAGE}
          </Text>
        </Stack>
      );
    case "failed":
      return (
        <PlayerDetailErrorState
          title={REPORTS_ERROR_TITLE}
          subtitle={REPORTS_ERROR_SUBTITLE}
          onRetry={props.onRetry}
          isRetrying={props.isRetrying}
        />
      );
    case "empty":
      return (
        <Text variant="body" className={getEmptyStateClass()}>
          No scouting reports yet. Add a report to get started.
        </Text>
      );
    case "ready":
      return (
        <Stack>
          {props.reports.map((report, index) => (
            <ScoutingReportRow
              key={report.id}
              report={report}
              isDark={props.isDark}
              isLastItem={index === props.reports.length - 1}
              onPress={props.onReportPress}
            />
          ))}
        </Stack>
      );
    default: {
      const exhaustive: never = props.readState;
      return exhaustive;
    }
  }
};

/**
 * ScoutingReportsListView renders the scouting reports list card
 * @param props - View props containing reports data and callbacks
 * @param props.isDark - Whether dark mode is active
 * @param props.reports - Array of visible scouting reports
 * @param props.allReports - Array of all scouting reports
 * @param props.reportCount - Total count of reports
 * @param props.hasMoreReports - Whether there are more reports
 * @param props.showViewAllModal - Whether to show the View All modal
 * @param props.onAddReport - Callback when Add Report is pressed (navigates to matches)
 * @param props.onReportPress - Callback when a report is pressed (navigates to reports page)
 * @param props.onViewAll - Callback when View all is pressed (opens modal)
 * @param props.onCloseViewAll - Callback when View All modal is closed
 * @param props.readState - Which read outcome the list body should draw (SE-7872)
 * @param props.onRetry - Retries the read behind this card from the error body
 * @param props.isRetrying - Whether a retry is in flight
 * @param props.isSideBySide - Whether the card is one of two side-by-side columns
 * @returns Box containing the scouting reports list layout
 */
const ScoutingReportsListView = ({
  isDark,
  reports,
  allReports,
  reportCount,
  hasMoreReports,
  showViewAllModal,
  onAddReport,
  onReportPress,
  onViewAll,
  onCloseViewAll,
  readState,
  onRetry,
  isRetrying,
  isSideBySide = false,
}: ScoutingReportsListViewProps) => (
  <Box className={getContainerClass(isSideBySide)}>
    <Stack className="gap-3">
      {/* Header row: icon, title, count, and view all link */}
      <Stack direction="horizontal" className="items-center justify-between">
        <Stack
          direction="horizontal"
          {...{ "data-testid": TEST_ID_PLAYER_DETAIL.SCOUTING_REPORTS_HEADING }}
          className="items-center gap-2"
        >
          <Icon as={FileText} size="sm" color={getIconColor(isDark)} />
          <Text variant="title-sm" className={getTitleClass()}>
            Scouting Reports
          </Text>
          {/* SE-7872: a count is only shown once the read settled. Rendering
              "0" beside a failed or in-flight read asserts a number nobody
              measured — the same false claim the empty copy used to make. */}
          {readState !== "loading" && readState !== "failed" && (
            <Box className={getCountBadgeClass()}>
              <Text variant="unstyled" className={getCountTextClass()}>
                {reportCount}
              </Text>
            </Box>
          )}
        </Stack>
        {hasMoreReports && (
          <Pressable onPress={onViewAll}>
            <Text variant="body-strong" className={getViewAllClass()}>
              View all
            </Text>
          </Pressable>
        )}
      </Stack>

      {/* Add new button on its own row */}
      <Button
        variant="outline"
        size="xs"
        onPress={onAddReport}
        className={getAddButtonClass()}
        label="Add new"
        labelTone="secondary"
        icon={Plus}
        iconTone="secondary"
      />

      {/* Reports list, empty copy, loading, or the failed body (SE-7872) */}
      {renderReportsBody({
        readState,
        isDark,
        reports,
        isRetrying,
        onRetry,
        onReportPress,
      })}

      {/* View All — full-screen overlay on mobile, centered modal on desktop */}
      <FullScreenOverlay
        isOpen={showViewAllModal}
        onClose={onCloseViewAll}
        title="All Scouting Reports"
        desktopSize="md"
      >
        <Stack>
          {allReports.map((report, index) => (
            <ScoutingReportRow
              key={report.id}
              report={report}
              isDark={isDark}
              isLastItem={index === allReports.length - 1}
              onPress={onReportPress}
            />
          ))}
        </Stack>
      </FullScreenOverlay>
    </Stack>
  </Box>
);

ScoutingReportsListView.displayName = "ScoutingReportsListView";

export default memo(ScoutingReportsListView);
